import React, { useState } from "react";
import {
  array,
  arrayOf,
  bool,
  func,
  int,
  number,
  obj,
  shape,
  string
} from "prop-types";

import {
  calculateCheckedAreas,
  calculateUncheckedAreas
} from "../../helpers/area.helper";
import AreaSelectorMap from "../AreaSelectorMap";
import BoroughNeighborhoods from "../BoroughNeighborhoods";
import BoroughTab from "../BoroughTab";
import * as S from "./styled";

const NeighborhoodSelector = ({
  allAreas,
  allowAutoSelecting,
  allowNesting,
  onSetSelectedAreas,
  selectedAreaIds,
  setSelectedAreaIds
}) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [hoveredAreaId, setHoveredAreaId] = useState(null);

  const levelOneAreas = allAreas.filter(area => area.level === 1);

  const nestingLevel =
    levelOneAreas[activeTabIndex].name === "Manhattan" ? 3 : 2;

  const levelTwoAreas = allAreas.filter(
    area => area.level > 1 && (allowNesting || area.level <= nestingLevel)
  );

  const currentTabChildren = allAreas
    .filter(area => area.parent_id === levelOneAreas[activeTabIndex].id)
    .sort((a, b) => (a.name > b.name ? 1 : -1));

  const updateSearchAreaDropdown = id => {
    const areaElement = global.document.getElementById(`area-${id}`);
    if (areaElement) {
      const elToTriggerChangeOn = areaElement;

      areaElement.checked = !areaElement.checked;

      elToTriggerChangeOn.dispatchEvent(new Event("change"));
    }
  };

  const handleSetSelectedAreas = async area => {
    updateSearchAreaDropdown(area.id);

    if (selectedAreaIds.includes(area.id)) {
      const checkedAreas = allAreas.filter(a => selectedAreaIds.includes(a.id));
      const ids = allowNesting
        ? calculateUncheckedAreas(checkedAreas, area, allowAutoSelecting)
        : calculateUncheckedAreas(
            checkedAreas,
            area,
            allowAutoSelecting
          ).filter(id => allAreas.find(a => a.id === id).level <= nestingLevel);

      if (onSetSelectedAreas) {
        await onSetSelectedAreas(area, ids);
      }

      const newSelectedAreas = selectedAreaIds.filter(id => !ids.includes(id));

      setSelectedAreaIds(newSelectedAreas);
    } else {
      const ids = allowNesting
        ? calculateCheckedAreas(allAreas, area, allowAutoSelecting)
        : calculateCheckedAreas(allAreas, area, allowAutoSelecting).filter(
            id => allAreas.find(a => a.id === id).level <= nestingLevel
          );

      if (onSetSelectedAreas) {
        await onSetSelectedAreas(
          area,
          // Need to send unique, not previously selected items
          ids.filter(id => !selectedAreaIds.includes(id))
        );
      }

      const newSelectedAreas = Array.from(
        new Set([...selectedAreaIds, ...ids])
      );

      setSelectedAreaIds(newSelectedAreas);
    }
  };

  return (
    <S.AreaSelectorContainer>
      <S.MapContainer>
        <S.AreaSelectorMapDiv>
          <AreaSelectorMap
            areas={allAreas}
            hoveredAreaId={hoveredAreaId}
            onClick={area => handleSetSelectedAreas(area)}
            onHover={id => setHoveredAreaId(id)}
            selectedAreaIds={selectedAreaIds}
          />
        </S.AreaSelectorMapDiv>
      </S.MapContainer>
      <S.CheckBoxContainer>
        <S.BoroughTabContainer>
          {levelOneAreas.map((area, index) => (
            <BoroughTab
              key={area.id}
              area={area}
              isChecked={selectedAreaIds.includes(area.id)}
              isHovered={index === activeTabIndex && area.id === hoveredAreaId}
              onCheck={selectedArea => handleSetSelectedAreas(selectedArea)}
              onClick={() => setActiveTabIndex(index)}
              onHover={id => setHoveredAreaId(id)}
              selected={index === activeTabIndex}
            />
          ))}
        </S.BoroughTabContainer>
        <BoroughNeighborhoods
          borough={levelOneAreas[activeTabIndex].name}
          hoveredAreaId={hoveredAreaId}
          levelTwoAreas={levelTwoAreas}
          neighborhoods={currentTabChildren}
          onCheck={ids => handleSetSelectedAreas(ids)}
          onHover={id => setHoveredAreaId(id)}
          selectedAreaIds={selectedAreaIds}
        />
      </S.CheckBoxContainer>
    </S.AreaSelectorContainer>
  );
};

NeighborhoodSelector.propTypes = {
  allAreas: arrayOf(
    shape({
      parent_id: int,
      decodedCoordinates: array,
      id: number,
      level: number,
      map_coordinates: obj,
      name: string,
      parent: obj
    })
  ).isRequired,
  allowAutoSelecting: bool,
  allowNesting: bool,
  onSetSelectedAreas: func,
  selectedAreaIds: arrayOf(number).isRequired,
  setSelectedAreaIds: func.isRequired
};

NeighborhoodSelector.defaultProps = {
  allowAutoSelecting: true,
  allowNesting: true,
  onSetSelectedAreas: null
};

export default NeighborhoodSelector;
